import { mobileDetected } from 'Shared/utils';
import { useContext, useEffect, memo, useMemo, ChangeEventHandler } from 'react';
import { useClusteredDataContext } from 'Guide/Schematics/Diagram/Providers/clusteredData';
import { ZoomContext } from 'Guide/Schematics/Diagram/Providers/d3Zoomable';
import { SearchInput } from 'Shared/search_input';
import { PartsList, PartListContainer } from './parts_list';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { KeypressContext } from '../Providers/keypressContext';
import { TooltipContext } from '../Providers/tooltipContext';
import { scaleAndCenterOnDataPoint } from 'Guide/Schematics/Diagram/Logic/scaleAndCenterOn';
import { useSearchContext } from 'Guide/Schematics/Diagram/Providers/searchContext';
import { fromWiki } from '../Generic/fromWiki';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { Button, Flex } from '@chakra-ui/react';

function Part({
   item,
   onClose,
}: {
   item: iFixitHotSpotItem;
   onClose: MinimalDisclosure['onClose'];
}) {
   const clusteredDataContext = useClusteredDataContext<iFixitHotSpotItem>();
   const zoomContext = useContext(ZoomContext);
   const tooltipContext = useContext(TooltipContext);

   const onClick = () => {
      // try tooltip opening before we tween
      tooltipContext.setOpenDataPointId(item.datapointid);
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
      scaleAndCenterOnDataPoint(item.datapointid, clusteredDataContext, zoomContext).then(() => {
         //ensure tooltip is open after tween
         tooltipContext.setOpenDataPointId(item.datapointid);
      });

      const onMobile = mobileDetected();

      if (onMobile) {
         // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
         onClose();
      }
   };

   const sku = fromWiki(item, 'sku');
   const name = fromWiki(item, 'name') || item.description;

   return (
      <Button
         variant="link"
         display="flex"
         flexDirection="column"
         flexWrap="wrap"
         whiteSpace="normal"
         width="100%"
         padding="12px 16px"
         color="gray.800"
         lineHeight="normal"
         textDecoration="none"
         fontWeight="normal"
         borderRadius="none"
         alignItems="flex-start"
         textAlign="start"
         _hover={{
            backgroundColor: 'gray.200',
            textDecoration: 'none',
         }}
         _focus={{
            backgroundColor: 'gray.200',
            textDecoration: 'none',
            outline: 'none',
         }}
         onClick={onClick}
      >
         <Flex fontSize="16px" color="gray.800">
            {name}
         </Flex>
         {sku && (
            <Flex fontSize="14px" color="gray.600">
               {sku}
            </Flex>
         )}
      </Button>
   );
}

const registerShortCuts = (
   isOpen: MinimalDisclosure['isOpen'],
   onClose: MinimalDisclosure['onClose']
) => {
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useContext" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   const keypressContext = useContext(KeypressContext);

   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEffect" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   useEffect(() => {
      const mappings = [{ id: 'PartsList', key: 'Escape', action: onClose }];

      if (isOpen) {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.pushMappings(mappings);
      } else {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      }

      return () => {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      };
   }, [isOpen]);
};

const getPartRow = (onClose: MinimalDisclosure['onClose']) =>
   memo(function getPartRowInternal({ datapoint }: { datapoint: iFixitHotSpotItem }) {
      return <Part item={datapoint} onClose={onClose} />;
   });

function FloatingPartsList() {
   const topMargin = '110px';
   const bottomMargin = '32px';
   const contentMargin = '32px';
   const widthMargin = '32px';

   const searchContext = useSearchContext<iFixitHotSpotItem>();
   const searchText = searchContext.searchText;
   const { isOpen, onClose } = searchContext.disclosure;
   const filteredData = searchContext.filteredData;

   registerShortCuts(isOpen, onClose);

   const onChange: ChangeEventHandler<HTMLInputElement> = event => {
      searchContext.setSearchText(event.target.value);
   };

   const PartRow = useMemo(() => getPartRow(onClose), [onClose]);

   if (!isOpen) {
      return null;
   }

   return (
      <PartListContainer marginRight={widthMargin} marginTop={topMargin} width="300px">
         <SearchInput value={searchText} onChange={onChange} containerProps={{ margin: '16px' }} />
         <PartsList
            data={filteredData}
            PartRow={PartRow}
            maxHeight={`calc(100vh - ${topMargin} -  (2 * ${bottomMargin}) - ${contentMargin})`}
         />
      </PartListContainer>
   );
}

export default memo(FloatingPartsList);
